import React, { useState } from 'react';
import {DataGrid} from '@material-ui/data-grid';
import CameraEventRepository from "../../../repositories/CameraEventRepository";
import Header from "./Header";
import {useGrid} from "../../../hooks/crud/Grid";
import {useTranslation} from "react-i18next";
import {GridSkeleton} from "../../../components/GridSkeleton";
import DateFnsUtils from '@date-io/date-fns';
import {  MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format } from 'date-fns'
import {
    ActionButtons,
    ActionButtonTypes,
    buildActionButton,
    CameraEventDirectionColumnType,
    CameraEventSuspiciousColumnType,
    EditableNumberPlateColumnType,
    EventDateColumnType,
    NumberPlateImageUrlColumnType, ParkingEventLinkColumnType
} from "../../../components/Grid/Columns";
import LprEventDetailView from "../View";
import ChangesPreview from "../ChangesPreview";
import Filter from "./Filter";
import ParkingEventDetailView from "../../ParkingEvent/View";
import { saveAs } from 'file-saver'
import AdjacentEventsView from "../AdjacentEvents";

const LprEventListView = () => {

	const repository = new CameraEventRepository();
	const [openedCameraEventId, setOpenedCameraEventId] = useState(null);
	const [openedParkingEventId, setOpenedParkingEventId] = useState(null);
    const [openedAdjacentEventsId, setOpenedAdjacentEventsId] = useState(null);
	const {t}  = useTranslation();
    const [showPreviewChanges, setShowPreviewChanges] = useState(null);
	const [blockedWindow, setBlockedWindow] = useState(false);

    const today = format(new Date(), 'yyyy-MM-dd');

    const hourFrom = format(new Date(), 'HH');

    const defaultFilter = { DateFrom: today, DateTo: today, HourFrom: hourFrom };

	const onViewHandler = (e) => {
	    setOpenedCameraEventId(e.Id);
    }

    const onViewParkingEventHandler = (e) => {
	    setOpenedParkingEventId(e.PairId);
    }

    const onAdjacentEventsHandler = (e) => {
        setOpenedAdjacentEventsId(e.Id);
    }

    const handleNumberPlateEdit = async (e) => {
	    const updateData = {Id: e.id, NumberPlate: e.newValue};

	    if (e.oldValue != e.newValue) {
	        setShowPreviewChanges(updateData);
        }
    }

	const {items,
        totalCount,
        itemsOnPage,
        onPage,
        isSearching,
        onFilterChangedHandler,
        onSortChangedHandler,
        filter,
        sort,
        setFilterValue,
        updateGrid} = useGrid(repository, { defaultFilter: defaultFilter, defaultSort: "Id|desc" });

	const columns = [
      { field: 'Id', headerName: t('ID'), width: 100, sortable: false },
      { field: 'Direction', headerName: t('Direction'), ...CameraEventDirectionColumnType({onClick: onViewHandler}) },
      { field: 'EventDate', type: "dateTime",  headerName: t('Event date'), ... EventDateColumnType},
      { field: 'NumberPlate', headerName: t('Number plate'), ...EditableNumberPlateColumnType({onChange: handleNumberPlateEdit}) },
      { field: 'NumberPlateImageUrl', headerName: t('Number plate photo'), ... NumberPlateImageUrlColumnType },
      { field: 'PairId', headerName: t('Pair'), width: 90, sortable: false, ... ParkingEventLinkColumnType({onClick: onViewParkingEventHandler})  },
      { field: 'ParkingZoneName', headerName: t('Zone'), width: 150, sortable: false },
      { field: 'CameraCode', headerName: t('Camera'), width: 120, sortable: false },
      { field: 'OcrConfidence', headerName: t('Confidence'), width: 85, sortable: false },
      {
          field: 'Suspicious',
          headerName: t('Suspicious'),
          ... CameraEventSuspiciousColumnType
      },
      {
          headerName: t('Actions'),
          field: 'view',
          ...ActionButtons([
              buildActionButton(ActionButtonTypes.VIEW, t("View details"), onViewHandler),
              buildActionButton(ActionButtonTypes.ADJACENT_EVENTS, t("Adjacent events"), onAdjacentEventsHandler, (row) => { return row.CameraCode != '' }),
              ])
      }
    ];

	const handleDetailViewClose = (e) => {
	    setOpenedCameraEventId(null);

	    if (e.isUpdated) {
	        onFilterChangedHandler({ filter: {} });
        }
    }

    const handleOnDownload = async (e) => {
	    setBlockedWindow(true);
	    try {
            const response = await repository.export(Object.assign({}, filter, { sort: sort }));

            const fileName = "[" + filter.DateFrom + " - " + filter.DateTo + "] - LPR Events.csv";
            saveAs(response.data, fileName);
        } catch (e) {
        } finally {
            setBlockedWindow(false);
        }
    }

	return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <>
                {openedCameraEventId && <LprEventDetailView id={openedCameraEventId} onClose={handleDetailViewClose} />}
                {openedParkingEventId && <ParkingEventDetailView id={openedParkingEventId} onClose={e => setOpenedParkingEventId(null)} />}
                {openedAdjacentEventsId && <AdjacentEventsView id={openedAdjacentEventsId} onClose={e => setOpenedAdjacentEventsId(null)} />}
                {showPreviewChanges && <ChangesPreview updateData={showPreviewChanges} onClose={e => setShowPreviewChanges(null)} onCancel={e => updateGrid() }  />}
	            <GridSkeleton title={t("Lpr events")}
                       isSearching={isSearching}
                       isBlocked={blockedWindow}
                       header={(<Header onRefresh={updateGrid} onDownload={handleOnDownload} />)}
                       filter={(<Filter filter={filter} onUpdateFilter={setFilterValue} onFilterChangedHandler={onFilterChangedHandler} onSortChangedHandler={onSortChangedHandler} sort={sort} />)}
                       grid={(<DataGrid
                                   disableMultipleSelection={true}
                                   disableColumnFilter
                                   rows={items()}
                                   rowCount={totalCount()}
                                   columns={columns}
                                   pageSize={itemsOnPage}
                                   paginationMode="server"
                                   sortingMode="server"
                                   disableColumnSelector={true}
                                   disableSelectionOnClick={true}
                                   disableColumnMenu={true}
                                   onPageChange={onPage}
                                   rowsPerPageOptions={[itemsOnPage]}
                                   hideFooterSelectedRowCount={true}
                         />)}
                       />
        </>
      </MuiPickersUtilsProvider>
	);
};

export default LprEventListView;
